import axios from 'axios';

export type BoatCreationReason =
  | 'ready'
  | 'missing-plan'
  | 'already-created'
  | 'admin-required'
  | 'boat-type-unavailable';

export interface ExistingBoatSummary {
  id: number;
  name: string;
  boat_id: number;
  boat_type: string;
}

export interface BoatCreationSetup {
  mustCreateBoat: boolean;
  canCreateBoat: boolean;
  reason: BoatCreationReason;
  ownedBoatCount: number;
  existingBoat: ExistingBoatSummary | null;
  subscriptionStatus: string;
  billingPeriod: string | null;
  isUnlimited: boolean;
  planBoatId: string | null;
  planBoatName: string | null;
  boatTypeId: number | null;
  boatTypeName: string | null;
}

export interface CreatedBoat {
  id: number;
  name: string;
  boat_id: number;
  boat_type: string;
  is_default?: boolean;
}

interface BoatSetupResponse {
  success: boolean;
  setup: BoatCreationSetup;
  message?: string;
}

interface CreateBoatResponse {
  success: boolean;
  boat: CreatedBoat;
  setup: BoatCreationSetup;
  message?: string;
}

const getApiBaseUrl = (): string => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!apiBaseUrl) {
    throw new Error('Boat setup endpoint is not configured.');
  }
  return apiBaseUrl;
};

const getAuthHeaders = (): { Authorization: string } => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Missing authentication token.');
  }
  return { Authorization: `Bearer ${token}` };
};

export const fetchMyBoatSetup = async (): Promise<BoatCreationSetup> => {
  const response = await axios.post<BoatSetupResponse>(
    `${getApiBaseUrl()}/api/user/getMyBoatSetup`,
    {},
    { headers: getAuthHeaders() },
  );
  if (!response.data.success || !response.data.setup) {
    throw new Error(response.data.message ?? 'Could not load boat setup.');
  }
  return response.data.setup;
};

export const createMyBoat = async (boatName: string): Promise<CreateBoatResponse> => {
  const response = await axios.post<CreateBoatResponse>(
    `${getApiBaseUrl()}/api/user/createMyBoat`,
    { boatName },
    { headers: getAuthHeaders() },
  );
  if (!response.data.success || !response.data.boat || !response.data.setup) {
    throw new Error(response.data.message ?? 'Could not create boat.');
  }
  return response.data;
};
